import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useState,
} from 'react';
import update from 'immutability-helper';
import { FormikProps } from 'formik';

import { useDebounce } from 'react-use';
import { ListenerEffectAPI, Dispatch } from '@reduxjs/toolkit';
import { useSelector, useDispatch } from 'react-redux';

const formikState: formikState = {
    path: '',
    values: {},
};

export enum FormikReduxActions {
    FORMIK_FIELD_REGISTER = 'FORMIK_FIELD_SET_REGISTER',
    FORMIK_FIELD_UNREGISTER = 'FORMIK_FIELD_SET_UNREGISTER',
    FORMIK_EFFECT_UNMOUNT = 'FORMIK_EFFECT_UNMOUNT',
    FORMIK_EFFECT_MOUNT = 'FORMIK_EFFECT_MOUNT',
    FORMIK_INCLUDE_PATH = 'FORMIK_INCLUDE_PATH',
}

export const setFieldRegister = (payload: any): any => ({
    type: FormikReduxActions.FORMIK_FIELD_REGISTER,
    payload,
});

export const setFieldUnregister = (payload: any): any => ({
    type: FormikReduxActions.FORMIK_FIELD_UNREGISTER,
    payload,
});

export const setEffectMountedInComponent = (payload: any): any => ({
    type: FormikReduxActions.FORMIK_EFFECT_MOUNT,
    payload,
});

export const setEffectMountedOutComponent = (payload: any): any => ({
    type: FormikReduxActions.FORMIK_EFFECT_UNMOUNT,
    payload,
});
export const setFormikPath = (payload: any): any => ({
    type: FormikReduxActions.FORMIK_INCLUDE_PATH,
    payload,
});

export const useFormikEffect = (
    fn: (values: any) => void,
    fieldList: Array<string>,
    path: string,
    rehydrate: boolean,
    timeout: number,
    deps: React.MutableRefObject<FormikProps<any> | null>
): void => {
    const { current: formik } = deps;
    const [isMounted, setMounted] = useState<boolean>(false);
    const [isInitial, setInitial] = useState<boolean>(false);
    const isPath = useSelector((state: any) => state.formikState.path);
    const values = useSelector((state: any) => state.formikState.values);
    const router = useSelector((state: any) => state.router);
    const dispatch = useDispatch();
    const [isUpdate, setUpdate] = useState<boolean>(false);
    const injectFieldValidation = useCallback(() => {
        const { current: anyFormik } = deps;
        setUpdate(true);
        dispatch(
            setFieldRegister({
                values: anyFormik?.values,
            })
        );
    }, [deps, dispatch]);
    const [, cancel] = useDebounce(
        () => {
            if (isMounted === false && path === isPath) {
                dispatch(setEffectMountedInComponent({ path }));
                setMounted(true);
            }

            // should desctructure for array element
            if (
                formik !== null &&
                isInitial === false &&
                router.location &&
                router.location.pathname === path
            ) {
                setInitial(true);
                fieldList.forEach((element) => {
                    formik.registerField(element, {
                        validate: injectFieldValidation,
                    });
                });
            }
        },
        timeout,
        []
    );

    // cancel formik listener and remove value.
    const handleCancelation = useCallback(() => {
        cancel();
        if (formik) {
            fieldList.forEach((element) => {
                formik.unregisterField(element);
            });
            dispatch(
                setFieldUnregister({
                    values: {},
                })
            );
        }
        // dispatch(setEffectMountedOutComponent({ path: '' }));
    }, [cancel, dispatch, fieldList, formik]);

    // callback when layout is  rehydrate formik state
    useLayoutEffect(() => {
        if (isMounted && rehydrate && formik) {
            if (typeof fn === 'function') {
                fn(values);
            }
        }

        return () => handleCancelation();
    }, [isMounted]);

    // callback in each iteration state when rehydrate false
    useEffect(() => {
        if (isUpdate && formik) {
            setUpdate(false);
            if (rehydrate === false) {
                if (typeof fn === 'function') {
                    fn(values);
                }
            }
        }

        return () => cancel();
    }, [isUpdate]);
};

export const targetFormikListener = '@@router/LOCATION_CHANGE';
export const useFormikEffectListener = async (
    action: any,
    listenerApi: ListenerEffectAPI<any, Dispatch>
): Promise<void> => {
    // Can cancel other running instances
    listenerApi.cancelActiveListeners();
    // Pause until action dispatched or state changed
    listenerApi.dispatch(setFormikPath(action.payload));
};

export const formikReducer = (
    state = formikState,
    action: any = { type: '', payload: {} }
): formikState => {
    switch (action.type) {
        case FormikReduxActions.FORMIK_EFFECT_MOUNT:
            return update(state, {
                $merge: {
                    ...action.payload,
                },
            });
        case FormikReduxActions.FORMIK_EFFECT_UNMOUNT:
            return update(state, {
                $merge: {
                    ...action.payload,
                },
            });
        case FormikReduxActions.FORMIK_FIELD_REGISTER:
            return update(state, {
                $merge: {
                    ...action.payload,
                },
            });
        case FormikReduxActions.FORMIK_FIELD_UNREGISTER:
            return update(state, {
                $merge: {
                    ...action.payload,
                },
            });
        case FormikReduxActions.FORMIK_INCLUDE_PATH:
            return update(state, {
                $merge: {
                    path: action.payload.location.pathname,
                },
            });
        default:
            return state;
    }
};
export const useFormikValidationEffect = (
    fn: (values: any) => void,
    field: string,
    deps: React.MutableRefObject<FormikProps<any> | null>
): void => {
    const handleInjectValidationFormik = useCallback(() => {
        const { current: formik } = deps;

        if (formik) {
            const { getFieldMeta } = formik;
            const { error, touched } = getFieldMeta(field);

            if (error && touched) {
                fn(false);
            } else {
                fn(true);
            }
        }
    }, [fn, deps, field]);

    useLayoutEffect(() => {
        const { current: formik } = deps;

        if (formik) {
            formik.registerField(field, {
                validate: handleInjectValidationFormik,
            });
        }
        return () => {
            if (formik) formik.unregisterField(field);
        };
    }, [handleInjectValidationFormik]);
};

export const useFormikElementOnValidationEffect = (
    fn: (values: any, element: Element | null) => void,
    fieldList: Array<string>,
    deps: React.MutableRefObject<FormikProps<any> | null>
): void => {
    const handleInjectValidationFormik = useCallback(
        (val: any, field: string) => {
            const { current: formik } = deps;

            if (formik) {
                const { getFieldMeta } = formik;
                const { error, touched } = getFieldMeta(field);

                const selector = document.querySelector(
                    `input[name="${field}"]`
                );
                if (error && touched) {
                    fn(false, selector);
                } else {
                    fn(true, selector);
                }
            }
        },
        [fn, deps]
    );

    useLayoutEffect(() => {
        const { current: formik } = deps;

        if (formik) {
            fieldList.forEach((element) => {
                formik.registerField(element, {
                    validate: (val) =>
                        handleInjectValidationFormik(val, element),
                });
            });
        }
        return () => {
            if (formik) {
                fieldList.forEach((element) => {
                    formik.unregisterField(element);
                });
            }
        };
    }, [handleInjectValidationFormik]);
};
